import { useEffect, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/product/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Filter, X, ChevronsRight } from "lucide-react";

export default function Shop() {
  const { category: catParam } = useParams();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ manufacturers: [], categories: [], regions: [] });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("featured");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  const category = catParam ? decodeURIComponent(catParam) : (params.get("category") || "");
  const manufacturer = params.get("manufacturer") || "";
  const make = params.get("make") || "";
  const region = params.get("region") || "";
  const q = params.get("q") || "";

  useEffect(() => {
    api.get("/products/facets").then(({ data }) => setFacets(data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = {
      page,
      limit: 24,
      sort,
      ...(category ? { category } : {}),
      ...(manufacturer ? { manufacturer } : {}),
      ...(make ? { make } : {}),
      ...(region ? { region } : {}),
      ...(q ? { q } : {}),
      ...(priceMin ? { min_price: priceMin } : {}),
      ...(priceMax ? { max_price: priceMax } : {}),
    };
    api.get("/products", { params: p })
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  }, [category, manufacturer, make, region, q, sort, page, priceMin, priceMax]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  const activeChips = [
    category && { k: "category", v: category },
    manufacturer && { k: "manufacturer", v: manufacturer },
    make && { k: "make", v: make },
    region && { k: "region", v: region },
    q && { k: "q", v: q },
  ].filter(Boolean);

  const Filters = () => (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Category</div>
        <div className="space-y-1.5 max-h-64 overflow-auto pr-2">
          {(facets.categories || []).map((c) => (
            <label key={c} className="flex items-center gap-2 text-sm">
              <Checkbox checked={category === c} onCheckedChange={(chk) => setFilter("category", chk ? c : "")} />
              <span className={category === c ? "text-[#0055FF] font-medium" : "text-slate-700"}>{c}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Manufacturer</div>
        <div className="space-y-1.5">
          {(facets.manufacturers || []).map((m) => (
            <label key={m} className="flex items-center gap-2 text-sm">
              <Checkbox checked={manufacturer === m} onCheckedChange={(chk) => setFilter("manufacturer", chk ? m : "")} />
              <span className={manufacturer === m ? "text-[#0055FF] font-medium" : "text-slate-700"}>{m}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Region</div>
        <div className="space-y-1.5">
          {["Domestic", "European", "Asian"].map((r) => (
            <label key={r} className="flex items-center gap-2 text-sm">
              <Checkbox checked={region === r} onCheckedChange={(chk) => setFilter("region", chk ? r : "")} />
              <span className={region === r ? "text-[#0055FF] font-medium" : "text-slate-700"}>{r}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">Price</div>
        <div className="flex gap-2">
          <Input type="number" placeholder="Min" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} data-testid="filter-price-min" />
          <Input type="number" placeholder="Max" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} data-testid="filter-price-max" />
        </div>
      </div>
      <Button variant="outline" className="w-full" onClick={() => { setParams({}); setPriceMin(""); setPriceMax(""); }} data-testid="filter-reset">
        Reset filters
      </Button>
    </div>
  );

  const totalPages = Math.max(1, Math.ceil(total / 24));

  return (
    <div data-testid="shop-page" className="bg-slate-50 min-h-screen">
      {/* Header strip */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
          <nav className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <Link to="/" className="hover:text-[#0055FF]">Home</Link>
            <ChevronsRight className="w-3 h-3" />
            <span className="text-slate-900">{category || "Shop all"}</span>
          </nav>
          <h1 className="mt-3 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
            {category || "Shop all products"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{total} product{total !== 1 ? "s" : ""} available</p>
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {activeChips.map((c) => (
                <button
                  key={c.k}
                  onClick={() => setFilter(c.k, "")}
                  data-testid={`chip-${c.k}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-xs text-slate-700"
                >
                  {c.v} <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block">
          <Filters />
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4 gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="lg:hidden gap-1.5" data-testid="mobile-filter-button">
                  <Filter className="w-4 h-4" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85%] sm:w-[380px] overflow-y-auto">
                <div className="mt-8"><Filters /></div>
              </SheetContent>
            </Sheet>
            <div className="ml-auto flex items-center gap-2">
              <div className="text-xs text-slate-500 font-mono uppercase tracking-widest hidden sm:block">Sort</div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[180px]" data-testid="shop-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price_asc">Price: Low to High</SelectItem>
                  <SelectItem value="price_desc">Price: High to Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="manufacturer">Manufacturer</SelectItem>
                  <SelectItem value="title">Product title</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, i) => (
                <Skeleton key={i} className="aspect-[4/3]" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
              <div className="text-slate-500">No products match these filters.</div>
              <Button className="mt-4" onClick={() => setParams({})}>Reset filters</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-testid="shop-results">
              {items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>Previous</Button>
              <span className="text-sm text-slate-500 font-mono">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
